/**
 * Pinia Store: Form entries (badge only)
 *
 * The list itself still lives in the old Vuex module; this store holds the
 * "nobody has opened it yet" count behind the menu badge, kept live over the
 * CMS broadcast channel the unread-message and open-ticket badges use.
 */

import { defineStore } from 'pinia';

import Echo from '@/echo';
import FormEntryApi from '@/extensions/forms/store/api/entry';

export const useFormEntryStore = defineStore('formEntry', {
    state: () => ({
        unreadCount: 0,
        realtimeCmsId: null,
        realtimeGeneration: 0,
    }),

    actions: {
        // A submission arrives without anyone in the admin doing anything, so
        // the count is pushed rather than polled.
        subscribeRealtime(cmsId) {
            if (!cmsId) return;
            // Same CMS on a rebuilt socket (mimic in/out) is not subscribed.
            if (this.realtimeCmsId === cmsId && this.realtimeGeneration === Echo.generation) {
                this.fetchUnreadCount(cmsId);
                return;
            }

            this.unsubscribeRealtime();
            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.realtimeCmsId = cmsId;
            this.realtimeGeneration = Echo.generation;
            Echo.client
                .private(`cms.${cmsId}`)
                .listen('.form-entries:count-changed', () => this.fetchUnreadCount(cmsId));

            this.fetchUnreadCount(cmsId);
        },

        unsubscribeRealtime() {
            // Only this store's own listener — the channel is shared with the
            // communication, ticket and presence stores, so never leave() it.
            if (this.realtimeCmsId && Echo.client) {
                try {
                    Echo.client.private(`cms.${this.realtimeCmsId}`).stopListening('.form-entries:count-changed');
                } catch (error) {
                    /* noop */
                }
            }
            this.realtimeCmsId = null;
        },

        fetchUnreadCount(cmsId) {
            return FormEntryApi.fetchUnreadCount(cmsId)
                .then((count) => {
                    this.unreadCount = count || 0;
                    return this.unreadCount;
                })
                .catch(() => {
                    this.unreadCount = 0;
                });
        },

        resetUnreadCount() {
            this.unreadCount = 0;
        },

        // Opening an entry marks it seen; `read: false` puts it back.
        markRead(id, read = true) {
            return FormEntryApi.markRead(id, read).then((entry) => {
                if (this.realtimeCmsId) this.fetchUnreadCount(this.realtimeCmsId);
                return entry;
            });
        },
    },
});
